import { ArrowLeft, TrendingUp, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "../../components/ui/button";
import homeService from "../../services/HomeService";
import { formatHealthInsightScore } from "../../utils/healthInsightsMetrics";
import { HEALTH_INSIGHT_SCORE_BANDS_BY_KEY } from "../../utils/healthInsightScoreBands";
import { HealthInsightScoreBandsList } from "../../components/HealthInsights/HealthInsightScoreBandsList";
import welcomeBg from "../../assets/homebg.png";
import BottomNavBar from "../../components/BottomNavBar";

// ─── Metric definitions ───────────────────────────────────────────────────────
const METRICS = [
  {
    key: "carb_fiber_ratio_daily" as const,
    title: "Carb-to-Fiber Ratio",
    shortDesc: {
      GOOD: "Great job! Keep carbs balanced with plenty of fiber.",
      MODERATE: "Increase fiber intake to better balance your carbs.",
      POOR: "Too many refined carbs. Add more whole grains and vegetables.",
    },
    whatIs: "This ratio compares total carbohydrates to dietary fiber in your diet. A lower ratio indicates higher fiber content relative to carbs, which is beneficial for gut health, blood sugar control, and sustained energy. Whole foods naturally have better ratios, while processed foods tend to have high ratios.",
  },
  {
    key: "bad_fat_ratio_daily" as const,
    title: "Fat Quality",
    shortDesc: {
      GOOD: "Great fat choices! Keep up the healthy unsaturated fats.",
      MODERATE: "Try replacing some saturated fats with healthier alternatives.",
      POOR: "Reduce saturated fats to lower cholesterol risks.",
    },
    whatIs: "Fat Quality measures the proportion of 'bad' fats (saturated + trans fats) relative to total fat intake. A lower percentage indicates a healthier fat profile. Unsaturated fats from nuts, fish, and olive oil support heart health, while high saturated fat raises LDL cholesterol and cardiovascular risk.",
  },
  {
    key: "sugar_ratio_daily" as const,
    title: "Sugar Load",
    shortDesc: {
      GOOD: "Sugar intake is well managed. Keep it up!",
      MODERATE: "Consider reducing sugary drinks and processed snacks.",
      POOR: "Sugar load is high. Watch out for sudden fatigue.",
    },
    whatIs: "Sugar Load measures the percentage of calories coming from added and free sugars. High sugar intake causes rapid glucose spikes followed by crashes, increases risk of insulin resistance, and contributes to weight gain. Keeping it under 10–15% of total calories supports metabolic health and stable energy.",
  },
  {
    key: "protein_fiber_ratio_daily" as const,
    title: "Protein-Fiber Ratio",
    shortDesc: {
      GOOD: "Perfect balance for muscle support and digestion.",
      MODERATE: "Add more high-fiber foods alongside your protein sources.",
      POOR: "Your protein is high but fiber is too low. Add more vegetables.",
    },
    whatIs: "This ratio compares protein intake to dietary fiber. A balanced ratio ensures that high protein consumption is accompanied by enough fiber for digestion, gut microbiome support, and cholesterol management. Animal proteins are naturally fiber-free, so pairing them with vegetables and legumes is key.",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Single source of truth for status colors so the badge AND the score icon
// always share the same color for a given status (e.g. EXCELLENT → green).
function getStatusStyle(label: string) {
  const l = label?.toUpperCase();
  if (l === "GOOD" || l === "EXCELLENT")
    return {
      text: "text-green-700 dark:text-green-400",
      bg: "bg-green-100 dark:bg-green-900/30",
      border: "border-green-300 dark:border-green-700",
      iconBg: "bg-green-100 dark:bg-green-900/30",
      iconColor: "text-green-500",
    };
  if (l === "POOR" || l === "HIGH" || l === "CRITICAL")
    return {
      text: "text-red-600 dark:text-red-400",
      bg: "bg-red-100 dark:bg-red-900/30",
      border: "border-red-300 dark:border-red-700",
      iconBg: "bg-red-100 dark:bg-red-900/30",
      iconColor: "text-red-500",
    };
  return {
    text: "text-orange-600 dark:text-orange-400",
    bg: "bg-orange-100 dark:bg-orange-900/30",
    border: "border-orange-300 dark:border-orange-700",
    iconBg: "bg-orange-100 dark:bg-orange-900/30",
    iconColor: "text-orange-500",
  };
}

function getShortDesc(cfg: typeof METRICS[0], label: string): string {
  const l = label?.toUpperCase();
  if (l === "GOOD" || l === "EXCELLENT") return cfg.shortDesc.GOOD;
  if (l === "POOR" || l === "HIGH" || l === "CRITICAL") return cfg.shortDesc.POOR;
  return cfg.shortDesc.MODERATE;
}

// ─── Page ─────────────────────────────────────────────────────────────────────
const HealthInsightsPage = () => {
  const navigate = useNavigate();
  const [openKey, setOpenKey] = useState<string | null>(null);

  const { data: homeData, isLoading } = useQuery({
    queryKey: ["homePageData"],
    queryFn: () => homeService.getHomePageData(),
    staleTime: 5 * 60 * 1000,
  });

  const insights = homeData?.["Health Insights"];

  const toggle = (key: string) =>
    setOpenKey(prev => (prev === key ? null : key));

  return (
    <div className="scrollbar-hide relative mx-auto flex h-dvh max-h-dvh w-full min-w-0 max-w-[400px] flex-col overflow-hidden bg-background">
      {/* Blurred background */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{ backgroundImage: `url(${welcomeBg})` }}
      />
      <div className="absolute inset-0 bg-background/85" />

      {/* Scrollable content */}
      <div className="scrollbar-hide relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-24 pt-3">

        {/* ── Header (centered title — matches Bite Balance / PageHeader bar pattern) ── */}
        <div className="mb-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10 shrink-0 rounded-xl border border-border bg-card"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1 text-center">
            <h1 className="text-title3 font-bold text-foreground">
              Health Insights
            </h1>
            <p className="text-caption1 text-muted-foreground">
              Track your daily nutrition ratios
            </p>
          </div>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <TrendingUp className="h-5 w-5 text-green-500" />
          </div>
        </div>

        {/* ── AI banner ── */}
        <div className="mb-4 flex items-center gap-2.5 rounded-xl border border-indigo-100 dark:border-indigo-800/30 bg-indigo-50 dark:bg-indigo-900/20 px-3.5 py-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/50">
            <Sparkles className="h-4 w-4 text-indigo-500" />
          </div>
          <p className="text-caption1 font-medium text-indigo-800 dark:text-indigo-300">
            <span className="font-bold">Tap any card</span> to learn more about each metric
          </p>
        </div>

        {/* ── Cards ── */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="animate-pulse rounded-2xl border border-border bg-card p-4">
                <div className="mb-3 flex items-center gap-2">
                  <div className="h-4 w-28 rounded bg-muted" />
                  <div className="h-5 w-14 rounded-full bg-muted" />
                </div>
                <div className="mb-2 h-7 w-20 rounded bg-muted" />
                <div className="h-3 w-full rounded bg-muted" />
              </div>
            ))}
          </div>
        ) : !insights ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/30">
              <Sparkles className="h-8 w-8 text-indigo-500" />
            </div>
            <p className="text-callout font-bold text-foreground">No insights yet</p>
            <p className="mt-1 text-subhead text-muted-foreground">Log your meals today to unlock health insights.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {METRICS.map(cfg => {
              const raw = insights[cfg.key];
              if (!raw) return null;

              const label       = raw.label ?? "—";
              const status      = getStatusStyle(label);
              const shortDesc   = getShortDesc(cfg, label);
              const scoreText   = formatHealthInsightScore(raw.score) ?? "—";
              const isOpen      = openKey === cfg.key;

              return (
                <div
                  key={cfg.key}
                  className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all"
                >
                  {/* ── Card header (always visible, tap to toggle) ── */}
                  <button
                    onClick={() => toggle(cfg.key)}
                    className="w-full text-left px-4 pt-4 pb-3"
                  >
                    {/* Title + badge + chevron */}
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-subhead font-semibold text-muted-foreground">{cfg.title}</span>
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-compact-sm font-bold uppercase tracking-wide ${status.text} ${status.bg} ${status.border}`}>
                          {label}
                        </span>
                      </div>
                      {isOpen
                        ? <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                        : <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />}
                    </div>

                    {/* Score + ratio */}
                    <div className="mb-1.5 flex items-center gap-2.5">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${status.iconBg}`}>
                        <TrendingUp className={`h-4 w-4 ${status.iconColor}`} />
                      </div>
                      <div className="min-w-0">
                        <span className="text-title1 font-bold text-foreground">{scoreText}</span>
                      </div>
                    </div>

                    {/* Short description */}
                    <p className="text-caption1 leading-relaxed text-muted-foreground">{shortDesc}</p>
                  </button>

                  {/* ── Accordion content ── */}
                  {isOpen && (
                    <div className="border-t border-border px-4 pb-4 pt-3 space-y-4">

                      {/* What is section */}
                      <div>
                        <p className="mb-1.5 text-subhead font-bold text-foreground">
                          What is {cfg.title}?
                        </p>
                        <p className="text-caption1 leading-relaxed text-muted-foreground">
                          {cfg.whatIs}
                        </p>
                      </div>

                      {/* Score bands */}
                      <div>
                        <p className="mb-2 text-caption1 font-semibold uppercase tracking-wide text-muted-foreground">
                          Score Ranges
                        </p>
                        <HealthInsightScoreBandsList
                          bands={HEALTH_INSIGHT_SCORE_BANDS_BY_KEY[cfg.key]}
                          variant="cards"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BottomNavBar />
    </div>
  );
};

export default HealthInsightsPage;
